import DatesXAxis from '@/components/common/charts/DatesXAxis';
import { toDollars } from '@/util/currency';
import { ExternalFinancialEntity, getName } from '@common/financialEntities';
import { feConfigs } from '@common/financialEntities/config';
import { dt } from '@common/util/dateTime';
import { isNumber } from '@common/util/types';
import { FEValueNode, Tick, TickRange, ValueSeries } from '@common/valueSeries';
import { Group } from '@visx/group';
import isEqual from 'lodash.isequal';
import React, { useCallback, useMemo } from 'react';
import {
    chartTheme,
    DEFAULT_MARGIN,
    useTickDatumGetterForDate,
    useTimeSeriesEntityData,
} from '../common/charts/chartHelper';
import {
    ChartTooltip,
    ChartTooltipLine,
    ChartTooltipRow,
} from '../common/charts/ChartTooltip';
import CursorTracker from '../common/charts/CursorTracker';
import { EntityLine } from '../common/charts/EntityLine';
import NumericYAxisWithGrid from '../common/charts/NumericYAxisWithGrid';
import { TodayLine, useTodayX } from '../common/charts/TodayLine';
import { useTickRangeNumericChartWithTooltip } from '../common/charts/useTickRangeNumericChartWithTooltip';

const feChartMargin = DEFAULT_MARGIN;

export interface FEChartProps {
    fe: ExternalFinancialEntity;
    width: number;
    height: number;
    range: TickRange;
    valueSeries: ValueSeries<FEValueNode>;
}

interface Datum extends Tick {
    value: number | null;
}

const FinancialEntityChart = React.memo(
    function FinancialEntityChart({
        fe,
        width,
        height,
        range,
        valueSeries,
    }: FEChartProps) {
        // Get FE values
        const name = getName(fe);
        const color = feConfigs[fe.type].color;

        const innerWidth = width - feChartMargin.left - feChartMargin.right;
        const innerHeight = height - feChartMargin.top - feChartMargin.bottom;

        const datumFromValueNode = useCallback((vn: FEValueNode) => {
            if (!isNumber(vn.balance)) return;
            return { ...vn, value: vn.balance, maxValue: vn.balance };
        }, []);

        const { data, recordedData, forecastData } = useTimeSeriesEntityData(
            range,
            valueSeries.values,
            datumFromValueNode
        );
        const tooltipDataForDate = useTickDatumGetterForDate(
            valueSeries,
            datumFromValueNode
        );
        const maxValueForDate = useCallback(
            (date: string) => tooltipDataForDate(date)?.value ?? 0,
            [tooltipDataForDate]
        );

        const yScaleDomain = useMemo(() => data.map((d) => d.value), [data]);

        const {
            xScale,
            yScale,
            tooltip,
            handleCursor,
            handleCursorLeave,
            handleTooltipMouseEnter,
        } = useTickRangeNumericChartWithTooltip({
            range,
            innerHeight,
            innerWidth,
            yScaleDomain,
            tooltipDataForDate,
            maxValueForDate,
            margin: feChartMargin,
        });

        const tooltipTop = tooltip.midpointY - 150;
        const todayX = useTodayX({ range, xScale });

        // memoized point getters for entity lines
        const xGetter = useCallback(
            (d: Datum) => xScale(d.date) ?? 0,
            [xScale]
        );
        const yGetter = useCallback(
            (d: Datum) => yScale(d.value ?? 0),
            [yScale]
        );

        if (innerWidth < 0 || innerHeight < 0) {
            return null;
        }

        return (
            <div style={{ width, height, position: 'relative' }}>
                <svg width={width} height={height}>
                    <Group left={feChartMargin.left} top={feChartMargin.top}>
                        <NumericYAxisWithGrid
                            scale={yScale}
                            width={innerWidth}
                            height={innerHeight}
                        />
                        <DatesXAxis
                            top={innerHeight}
                            scale={xScale}
                            unit={range.unit}
                        />
                        <EntityLine
                            data={recordedData}
                            x={xGetter}
                            y={yGetter}
                            color={color}
                            yScale={yScale}
                            solid
                        />
                        <EntityLine
                            data={forecastData}
                            x={xGetter}
                            y={yGetter}
                            color={color}
                            yScale={yScale}
                        />
                        <TodayLine todayX={todayX} innerHeight={innerHeight} />
                        <ChartTooltipLine
                            {...tooltip}
                            innerHeight={innerHeight}
                        />
                        <CursorTracker
                            w={innerWidth}
                            h={innerHeight}
                            onCursor={handleCursor}
                            onCursorLeave={handleCursorLeave}
                        />
                    </Group>
                </svg>

                {/* Render tooltip outside the svg for full html support */}
                {tooltip.visible && tooltip.data && (
                    <ChartTooltip
                        key={Math.random()}
                        top={tooltipTop}
                        left={tooltip.left}
                        title={dt(tooltip.data.date).format(
                            range.unit === 'day'
                                ? 'ddd, MMM DD YYYY'
                                : 'MMM, YYYY'
                        )}
                        zIndex={chartTheme.zIndex.tooltip}
                        headerRight={<></>}
                        onMouseEnter={handleTooltipMouseEnter}
                    >
                        <ChartTooltipRow
                            label={name}
                            labelColor={color}
                            value={
                                isNumber(tooltip.data.value)
                                    ? toDollars(tooltip.data.value)
                                    : ''
                            }
                        />
                    </ChartTooltip>
                )}
            </div>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.height === nextProps.height &&
            prevProps.width === nextProps.width &&
            prevProps.fe.updatedAt === nextProps.fe.updatedAt &&
            prevProps.fe.latestSnapshot?.id ===
                nextProps.fe.latestSnapshot?.id &&
            isEqual(prevProps.range, nextProps.range) &&
            isEqual(prevProps.valueSeries, nextProps.valueSeries)
        );
    }
);

export default FinancialEntityChart;
